import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { AlertCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Issues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  
  useEffect(() => {
    loadIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severityFilter]);
  
  const loadIssues = async () => {
    setLoading(true);
    try {
      const url = severityFilter !== 'all' 
        ? `${API}/issues?severity=${severityFilter}`
        : `${API}/issues`;
      
      const response = await axios.get(url);
      setIssues(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading issues:', error);
      toast.error('Fehler beim Laden der Issues');
      setLoading(false);
    }
  };
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };
  
  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high':
        return 'Hoch';
      case 'medium':
        return 'Mittel';
      case 'low':
        return 'Niedrig';
      default:
        return severity;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="issues-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Conversion Issues</h1>
            <p className="text-slate-600">Identifizierte Probleme und Optimierungspotenziale</p>
          </div>
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-slate-600" />
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-40" data-testid="severity-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="high">Hoch</SelectItem>
                <SelectItem value="medium">Mittel</SelectItem>
                <SelectItem value="low">Niedrig</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Issues List */}
      {issues.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center text-slate-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">Keine Issues gefunden</p>
              <p className="text-sm mt-2">Starte eine Analyse um Conversion-Probleme zu identifizieren</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {issues.map((issue) => (
            <Card key={issue.id} data-testid={`issue-${issue.id}`} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <CardTitle className="text-xl">{issue.title}</CardTitle>
                      <Badge className={getSeverityColor(issue.severity)} data-testid={`issue-severity-${issue.severity}`}>
                        {getSeverityLabel(issue.severity)}
                      </Badge>
                      <Badge variant="outline" className="font-mono text-xs">
                        Impact: {issue.impact_score}/10
                      </Badge>
                    </div>
                    <CardDescription className="text-sm">
                      {issue.page_url}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Beschreibung</h4>
                    <p className="text-sm text-slate-600">{issue.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-1">Ort</h4>
                      <p className="text-sm text-slate-600">{issue.location}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-1">Typ</h4>
                      <p className="text-sm text-slate-600 capitalize">{issue.issue_type.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Aktueller Zustand</h4>
                    <p className="text-sm text-slate-600">{issue.current_state}</p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-1">💡 Empfehlung</h4>
                    <p className="text-sm text-blue-800">{issue.recommendation}</p>
                  </div>
                  
                  <div className="text-xs text-slate-400">
                    Gefunden: {new Date(issue.timestamp).toLocaleString('de-DE')}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Issues;